/**
 * Dictate into a note or narrative. Native speech recognition in the store
 * apps, the Web Speech API in the browser (lib/native/speech.ts). What was
 * said is appended exactly as recognised (ADR-0011: free text stays as written).
 */
import { useEffect, useRef, useState } from 'react';
import { Button, useToast } from '../../components';
import { useI18n } from '../../lib/i18n';
import { listen, type Listening, type SpeechError } from '../../lib/native/speech';

export function VoiceNoteButton({
  currentNote,
  onAppend,
  disabled,
}: {
  currentNote: string;
  onAppend: (nextNote: string) => void;
  disabled?: boolean;
}) {
  const { t, lang } = useI18n();
  const toast = useToast();
  const [listening, setListening] = useState(false);
  const session = useRef<Listening | null>(null);
  const noteRef = useRef(currentNote);
  noteRef.current = currentNote;

  useEffect(() => () => session.current?.stop(), []);

  const toggle = async () => {
    if (listening) {
      session.current?.stop();
      return;
    }
    const s = listen(lang);
    session.current = s;
    setListening(true);
    try {
      const said = await s.result;
      if (said) {
        const prev = noteRef.current.trim();
        onAppend(prev ? `${prev} ${said}` : said);
      }
    } catch (e) {
      const code = e as SpeechError;
      toast.show(t(code === 'DENIED' ? 'ui.events.voice.denied' : code === 'UNSUPPORTED' ? 'ui.excellence.voice.unsupported' : 'ui.events.voice.failed'), 'warning');
    } finally {
      session.current = null;
      setListening(false);
    }
  };

  return (
    <Button type="button" size="sm" variant={listening ? 'danger' : 'ghost'} icon="languages" disabled={disabled} onClick={toggle}>
      {listening ? t('ui.excellence.voice.stop') : t('ui.excellence.voice.start')}
    </Button>
  );
}
